import type { EyeParameters } from "@/models/EyeParameters";

export interface IcbAlert {
  label: string;
  diff: number;
  status: 'green' | 'yellow' | 'red' | undefined;
  points: number;
  od: number;
  os: number;
  message: string;
  unit: string;
  limits: { green: number; yellowMin: number; yellowMax: number; red: number };
}

export interface IcbInfo {
  alerts: Record<string, IcbAlert>;
  score: number;
  classification: string;
}

export function computeIcbInfo(
  od: EyeParameters | undefined,
  os: EyeParameters | undefined,
  t: (key: string) => string
): IcbInfo | null {
  if (!od || !os) {
    return null;
  }

  const hasVal = (v: any) => typeof v === 'number' && v > 0;

  const alerts: Record<string, IcbAlert> = {
    al: {
      label: 'AL',
      diff: 0,
      status: undefined,
      points: 30,
      od: 0,
      os: 0,
      message: '',
      unit: 'mm',
      limits: { green: 0.30, yellowMin: 0.30, yellowMax: 0.60, red: 0.60 }
    },
    km: {
      label: 'Km',
      diff: 0,
      status: undefined,
      points: 25,
      od: 0,
      os: 0,
      message: '',
      unit: 'D',
      limits: { green: 0.50, yellowMin: 0.50, yellowMax: 1.00, red: 1.00 }
    },
    acd: {
      label: 'ACD',
      diff: 0,
      status: undefined,
      points: 20,
      od: 0,
      os: 0,
      message: '',
      unit: 'mm',
      limits: { green: 0.40, yellowMin: 0.40, yellowMax: 1.00, red: 1.00 }
    },
    lt: {
      label: 'LT',
      diff: 0,
      status: undefined,
      points: 15,
      od: 0,
      os: 0,
      message: '',
      unit: 'mm',
      limits: { green: 0.60, yellowMin: 0.60, yellowMax: 1.20, red: 1.20 }
    }
  };

  // 1. AL
  if (hasVal(od.al) && hasVal(os.al)) {
    alerts.al.od = od.al;
    alerts.al.os = os.al;
    alerts.al.diff = Math.round(Math.abs(od.al - os.al) * 100) / 100;
    if (alerts.al.diff < 0.30) {
      alerts.al.status = 'green';
      alerts.al.points = 30;
      alerts.al.message = t('icb.al_msg_green');
    } else if (alerts.al.diff <= 0.60) {
      alerts.al.status = 'yellow';
      alerts.al.points = 15;
      alerts.al.message = t('icb.al_msg_yellow');
    } else {
      alerts.al.status = 'red';
      alerts.al.points = 0;
      alerts.al.message = t('icb.al_msg_red');
    }
  }

  // 2. Km
  if (hasVal(od.k.k1.k) && hasVal(od.k.k2.k) && hasVal(os.k.k1.k) && hasVal(os.k.k2.k)) {
    const kmOd = (od.k.k1.k + od.k.k2.k) / 2;
    const kmOs = (os.k.k1.k + os.k.k2.k) / 2;
    alerts.km.od = kmOd;
    alerts.km.os = kmOs;
    alerts.km.diff = Math.round(Math.abs(kmOd - kmOs) * 100) / 100;
    if (alerts.km.diff < 0.50) {
      alerts.km.status = 'green';
      alerts.km.points = 25;
      alerts.km.message = t('icb.km_msg_green');
    } else if (alerts.km.diff <= 1.00) {
      alerts.km.status = 'yellow';
      alerts.km.points = 12.5;
      alerts.km.message = t('icb.km_msg_yellow');
    } else {
      alerts.km.status = 'red';
      alerts.km.points = 0;
      alerts.km.message = t('icb.km_msg_red');
    }
  }

  // 3. ACD
  if (hasVal(od.acd) && hasVal(os.acd)) {
    alerts.acd.od = od.acd;
    alerts.acd.os = os.acd;
    alerts.acd.diff = Math.round(Math.abs(od.acd - os.acd) * 100) / 100;
    if (alerts.acd.diff < 0.40) {
      alerts.acd.status = 'green';
      alerts.acd.points = 20;
      alerts.acd.message = t('icb.acd_msg_green');
    } else if (alerts.acd.diff <= 1.00) {
      alerts.acd.status = 'yellow';
      alerts.acd.points = 10;
      alerts.acd.message = t('icb.acd_msg_yellow');
    } else {
      alerts.acd.status = 'red';
      alerts.acd.points = 0;
      alerts.acd.message = t('icb.acd_msg_red');
    }
  }

  // 4. LT
  if (hasVal(od.lt) && hasVal(os.lt)) {
    alerts.lt.od = od.lt;
    alerts.lt.os = os.lt;
    alerts.lt.diff = Math.round(Math.abs(od.lt - os.lt) * 100) / 100;
    if (alerts.lt.diff < 0.60) {
      alerts.lt.status = 'green';
      alerts.lt.points = 15;
      alerts.lt.message = t('icb.lt_msg_green');
    } else if (alerts.lt.diff <= 1.20) {
      alerts.lt.status = 'yellow';
      alerts.lt.points = 7.5;
      alerts.lt.message = t('icb.lt_msg_yellow');
    } else {
      alerts.lt.status = 'red';
      alerts.lt.points = 0;
      alerts.lt.message = t('icb.lt_msg_red');
    }
  }

  const score = alerts.al.points + alerts.km.points + alerts.acd.points + alerts.lt.points;

  let classification = 'excellent';
  if (score >= 90) {
    classification = 'excellent';
  } else if (score >= 70) {
    classification = 'review';
  } else if (score >= 50) {
    classification = 'moderate';
  } else {
    classification = 'high';
  }

  return {
    alerts,
    score,
    classification
  };
}

export function getActiveAlerts(icbInfo: IcbInfo | null) {
  if (!icbInfo) return [];
  const list = [];
  const keys = ['al', 'km', 'acd', 'lt'] as const;

  for (const key of keys) {
    const alert = icbInfo.alerts[key];
    if (alert.status !== undefined) {
      list.push({
        key,
        label: alert.label,
        status: alert.status,
        diff: alert.diff,
        od: alert.od,
        os: alert.os,
        message: alert.message,
        unit: alert.unit,
        limits: alert.limits
      });
    }
  }
  return list;
}
